import { useState } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { FluentEmoji } from '@/components/ui/fluent-emoji'
import { EMOJI_MAP } from '@/lib/fluent-emoji'

// Get emojis that have STATIC versions (EMOJI_MAP) - these display correctly in the picker
// We only use EMOJI_MAP because the picker shows static emojis (not animated)
const STATIC_EMOJIS = new Set(Object.keys(EMOJI_MAP))

// Helper to filter emojis to only those with static 3D versions
const filterEmojis = (emojis: string[]) => emojis.filter(e => STATIC_EMOJIS.has(e))

// Comprehensive emoji categories for habit picker - ONLY emojis from our Fluent library
export const HABIT_EMOJI_CATEGORIES: { name: string; emojis: string[]; keywords: string[] }[] = [
  {
    name: '🔥 Popular',
    emojis: filterEmojis(['🔥', '💪', '🏃', '📚', '🧘', '✨', '🎯', '⭐', '🚀', '💡', '❤️', '✅', '🏆', '💎', '🙏', '📝', '💧', '⏰', '🌅', '☀️', '💯', '🎉', '👏', '🙌', '👑']),
    keywords: ['popular', 'common', 'fire', 'strong', 'run', 'read', 'meditate', 'star', 'goal', 'trophy', 'water', 'alarm', 'morning'],
  },
  {
    name: '🏋️ Fitness',
    emojis: filterEmojis(['🏃', '🏋️', '🚴', '🏊', '🧘', '💪', '🤸', '⚽', '🏀', '🎾', '🥊', '🏌️', '🧗', '🏂', '⛷️', '🎿', '🛹', '🏇', '🤾', '🏐', '🏈', '⚾', '🥎', '🏉', '🥏', '🎳', '🏏', '🏑', '🏒', '🥍', '🏓', '🏸', '⛳', '⛸️', '🛷', '🥌', '🥋']),
    keywords: ['fitness', 'exercise', 'gym', 'workout', 'run', 'jog', 'lift', 'swim', 'bike', 'cycle', 'yoga', 'stretch', 'sport', 'basketball', 'soccer', 'tennis', 'boxing', 'golf', 'ski', 'skate', 'strong', 'muscle', 'cardio', 'training'],
  },
  {
    name: '📚 Learning',
    emojis: filterEmojis(['📚', '📖', '📝', '✍️', '📓', '📕', '📗', '📘', '📙', '🖊️', '✏️', '🖋️', '📰', '💻', '🎹', '🎸', '🎷', '🎺', '🎻', '🥁', '📔', '📒', '📑', '🔖', '📜', '📃', '📄', '🗃️', '💡', '🌐', '🗺️']),
    keywords: ['learn', 'read', 'study', 'write', 'book', 'journal', 'note', 'school', 'education', 'code', 'program', 'music', 'piano', 'guitar', 'language', 'practice', 'homework', 'research', 'knowledge', 'smart', 'brain', 'think'],
  },
  {
    name: '🧘 Mindfulness',
    emojis: filterEmojis(['🧘', '🙏', '💭', '🌸', '🌿', '🍃', '🛁', '😌', '😊', '🌙', '✨', '🔮', '🧿', '🪴', '🌺', '🌻', '🌼', '🌷', '🌹', '🌱', '🌾', '☁️', '🌈', '🕊️', '🦋']),
    keywords: ['mindful', 'meditate', 'meditation', 'calm', 'peace', 'relax', 'breathe', 'zen', 'yoga', 'pray', 'spiritual', 'soul', 'mind', 'mental', 'therapy', 'self-care', 'wellness', 'healing', 'nature', 'flower', 'plant', 'bath', 'spa'],
  },
  {
    name: '🥗 Nutrition',
    emojis: filterEmojis(['🥗', '💧', '🧃', '🧊', '☕', '🍕', '🍔', '🍟', '🌭', '🍿', '🧁', '🍰', '🎂', '🍩', '🍪', '🍫', '🍬', '🍺', '🍻', '🥂', '🍾', '🍷', '🥃']),
    keywords: ['food', 'eat', 'diet', 'nutrition', 'healthy', 'water', 'drink', 'hydrate', 'cook', 'meal', 'breakfast', 'lunch', 'dinner', 'coffee'],
  },
  {
    name: '😴 Sleep & Rest',
    emojis: filterEmojis(['😴', '🛏️', '🌙', '⭐', '🌟', '💤', '🌃', '🌉', '🌌', '🔕', '⏰', '🔋', '☕', '🌛', '✨', '🛋️']),
    keywords: ['sleep', 'rest', 'bed', 'night', 'nap', 'dream', 'wake', 'morning', 'alarm', 'early', 'tired', 'energy', 'recover', 'relax', 'routine', 'bedtime'],
  },
  {
    name: '💼 Work & Productivity',
    emojis: filterEmojis(['📊', '📈', '💻', '🖥️', '⌨️', '🖱️', '📱', '📧', '✉️', '📬', '📭', '📮', '🗂️', '📁', '📂', '🗃️', '📋', '📌', '📍', '✂️', '📎', '🖇️', '✅', '🗓️', '📅', '⏰', '🎯', '🚩', '💰', '🤝']),
    keywords: ['work', 'job', 'office', 'business', 'productivity', 'focus', 'task', 'project', 'meeting', 'email', 'computer', 'laptop', 'phone', 'schedule', 'deadline', 'goal', 'target', 'money', 'career', 'professional'],
  },
  {
    name: '🎨 Creative',
    emojis: filterEmojis(['🎨', '🖌️', '🖍️', '✏️', '🎬', '📷', '📸', '🎥', '📹', '🎤', '🎧', '🎹', '🎸', '🎺', '🎻', '🥁', '🎷', '📝', '✍️', '🖊️', '🖋️', '🎠', '🎡', '🎢', '🧩', '🎲', '🎰', '🎮', '🎁', '🎱']),
    keywords: ['create', 'art', 'paint', 'draw', 'design', 'music', 'sing', 'dance', 'photo', 'video', 'film', 'write', 'craft', 'hobby', 'instrument', 'guitar', 'piano', 'drum', 'camera', 'creative', 'imagine', 'make'],
  },
  {
    name: '💚 Health',
    emojis: filterEmojis(['💚', '❤️', '🏥', '🚑', '🏃', '💪', '🧘', '🥶', '🍬']),
    keywords: ['health', 'medical', 'doctor', 'medicine', 'hospital', 'heart', 'body', 'checkup', 'wellness', 'physical'],
  },
  {
    name: '🏠 Home & Cleaning',
    emojis: filterEmojis(['🏠', '🏢', '🏬', '🧹', '🧺', '🧼', '🪣', '🧴', '🪥', '🛁', '🚿', '🚽', '🛋️', '🪑', '🛏️', '🪟', '🚪', '🪞', '🖼️', '🛒', '📦', '🗑️', '🌱', '🪴', '🌿', '🌻', '🌺', '🌸', '🌼', '🌷']),
    keywords: ['home', 'house', 'clean', 'tidy', 'organize', 'laundry', 'wash', 'dishes', 'vacuum', 'mop', 'sweep', 'declutter', 'minimalist', 'garden', 'plant', 'water', 'chore', 'domestic', 'room', 'bathroom', 'kitchen', 'bedroom'],
  },
  {
    name: '👥 Social',
    emojis: filterEmojis(['👥', '🤝', '❤️', '💕', '💞', '💓', '💗', '💖', '💝', '🥰', '😍', '😘', '🤗', '👋', '🙋', '🙌', '👏', '🎉', '🎊', '🎂', '🎁', '📞', '📱', '💬', '💭']),
    keywords: ['social', 'friend', 'family', 'relationship', 'love', 'partner', 'call', 'talk', 'chat', 'connect', 'network', 'community', 'together', 'date', 'meeting', 'party', 'celebrate', 'hug', 'support', 'help', 'care'],
  },
  {
    name: '💰 Finance',
    emojis: filterEmojis(['💰', '🏦', '📈', '📊', '💎', '🛍️', '🛒', '🏷️', '📱', '💻', '🖥️', '🏠']),
    keywords: ['money', 'finance', 'budget', 'save', 'invest', 'spend', 'bank', 'cash', 'credit', 'debt', 'income', 'expense', 'wealth', 'rich', 'stock', 'trade', 'tax', 'bill', 'payment'],
  },
  {
    name: '🎯 Goals & Achievement',
    emojis: filterEmojis(['🎯', '🏆', '🥇', '🥈', '🥉', '🏅', '🎖️', '📜', '📃', '✅', '✔️', '💯', '🔥', '⭐', '🌟', '✨', '💫', '🚀', '📈', '🎉', '🎊', '🙌', '👏', '💪', '👑', '💎', '⬆️', '📌', '🎁', '🪄']),
    keywords: ['goal', 'target', 'achieve', 'win', 'success', 'trophy', 'medal', 'award', 'prize', 'champion', 'first', 'best', 'top', 'complete', 'finish', 'done', 'accomplish', 'milestone', 'progress', 'level', 'rank'],
  },
  {
    name: '🌍 Nature & Outdoors',
    emojis: filterEmojis(['🌍', '🗺️', '🏔️', '⛰️', '🌋', '🏕️', '🏖️', '🏜️', '🏝️', '🌲', '🌳', '🌴', '🌵', '🌾', '🌿', '☘️', '🍀', '🍁', '🍂', '🍃', '🌺', '🌻', '🌼', '🌷', '🌹', '🪺', '🐦', '🦅', '🦆', '🦢', '🦩', '🦚', '🦜', '🌅', '🌄', '🌌', '🌙', '☀️', '🌤️', '⛅', '🌈', '🌊', '💧', '❄️', '☃️', '⛄']),
    keywords: ['nature', 'outdoor', 'outside', 'hike', 'camp', 'mountain', 'beach', 'forest', 'tree', 'flower', 'garden', 'park', 'walk', 'explore', 'adventure', 'travel', 'trip', 'vacation', 'sun', 'sunrise', 'sunset', 'moon', 'star', 'sky', 'weather'],
  },
  {
    name: '🐾 Pets & Animals',
    emojis: filterEmojis(['🐕', '🐶', '🐩', '🐈', '🐱', '🐇', '🐰', '🐹', '🐭', '🐀', '🦔', '🦫', '🐻', '🐨', '🐼', '🦥', '🦦', '🦘', '🦡', '🦁', '🐯', '🐘', '🦛', '🦏', '🐪', '🐫', '🦒', '🐂', '🐄', '🐎', '🐖', '🐏', '🐑', '🦙', '🐐', '🦌', '🦃', '🐓', '🐔', '🐦', '🐧', '🕊️', '🦅', '🦆', '🦢', '🦉', '🦩', '🦚', '🦜', '🐸', '🐊', '🐢', '🦎', '🐍', '🐲', '🐉', '🐳', '🐋', '🐬', '🐟', '🐠', '🦈', '🐙', '🐌', '🦋', '🐛', '🐜', '🐝', '🐞', '🦟']),
    keywords: ['pet', 'animal', 'dog', 'cat', 'bird', 'fish', 'hamster', 'rabbit', 'walk', 'feed', 'care', 'vet', 'train', 'play', 'love', 'companion', 'puppy', 'kitten', 'aquarium', 'zoo', 'wildlife', 'nature'],
  },
  {
    name: '🎉 Celebration',
    emojis: filterEmojis(['🎉', '🎊', '🥳', '🎂', '🎁', '✨', '💫', '🌟', '⭐', '🏆', '🥇', '🎖️', '🏅', '👑', '💯', '🔥', '💥', '🙌', '👏', '🤝', '😄', '😃', '😁', '🥰', '😍', '🤩', '🥂', '🍾', '🍻', '🍺', '🍷', '🧁', '🍰', '🎂', '🍩', '🍪', '🍫', '🍬']),
    keywords: ['celebrate', 'party', 'birthday', 'anniversary', 'holiday', 'festival', 'happy', 'joy', 'fun', 'congratulations', 'success', 'win', 'achieve', 'milestone', 'special', 'occasion', 'cheers', 'toast'],
  },
  {
    name: '🧪 Science & Tech',
    emojis: filterEmojis(['🚀', '🤖', '🔋', '🔌', '💡', '💻', '🖥️', '🖨️', '⌨️', '🖱️', '💾', '💿', '📀', '📱', '📲', '☎️', '📞', '📟', '📠', '🔍', '🔎', '⚙️']),
    keywords: ['science', 'tech', 'technology', 'computer', 'code', 'program', 'developer', 'engineer', 'research', 'lab', 'experiment', 'data', 'ai', 'robot', 'software', 'hardware', 'phone', 'app', 'digital', 'innovation', 'future', 'space', 'rocket'],
  },
  {
    name: '🎭 Emotions',
    emojis: filterEmojis(['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '🤗', '🤭', '🤫', '🤔', '😏', '😌', '😴', '🤤', '😷', '🤒', '🤕', '🤮', '🥴', '😵', '🤯', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '😈', '👿', '💀', '☠️', '👻', '👽', '🤖', '💩', '🙈', '🙉', '🙊', '🥶']),
    keywords: ['emotion', 'feel', 'happy', 'sad', 'angry', 'excited', 'nervous', 'scared', 'love', 'smile', 'laugh', 'cry', 'mood', 'mental', 'therapy', 'grateful', 'positive', 'negative', 'anxiety', 'stress', 'calm', 'peace'],
  },
]

// Flatten all emojis for quick access
export const ALL_HABIT_EMOJIS = HABIT_EMOJI_CATEGORIES.flatMap(cat => cat.emojis).filter((v, i, a) => a.indexOf(v) === i)

// Search function that returns matching emojis
function searchEmojis(query: string): string[] {
  if (!query.trim()) return HABIT_EMOJI_CATEGORIES[0].emojis
  const lowerQuery = query.toLowerCase()
  const matches = new Set<string>()
  
  for (const category of HABIT_EMOJI_CATEGORIES) {
    const categoryMatches = category.keywords.some(kw => kw.includes(lowerQuery) || lowerQuery.includes(kw))
    if (categoryMatches) {
      category.emojis.forEach(e => matches.add(e))
    }
  }
  
  return Array.from(matches).slice(0, 50)
}

interface EmojiPickerSectionProps {
  emoji: string | null
  onSelect: (emoji: string | null) => void
  /** If true, shows a "Remove" option to clear the emoji */
  allowRemove?: boolean
  /** Custom trigger label when no emoji is selected */
  emptyLabel?: string
  /** Custom trigger label when emoji is selected */
  changeLabel?: string
}

// Reusable Emoji Picker Component with Search
export function EmojiPickerSection({ 
  emoji, 
  onSelect, 
  allowRemove = false,
  emptyLabel = 'Add icon',
  changeLabel = 'Change',
}: EmojiPickerSectionProps) {
  const [emojiSearch, setEmojiSearch] = useState('')
  const [open, setOpen] = useState(false)
  
  // Get emojis to display - search results or all emojis
  const displayEmojis = emojiSearch 
    ? searchEmojis(emojiSearch)
    : ALL_HABIT_EMOJIS
  
  const handleSelect = (selectedEmoji: string) => {
    onSelect(selectedEmoji)
    setOpen(false)
    setEmojiSearch('')
  }

  const handleRemove = () => {
    onSelect(null)
    setOpen(false)
    setEmojiSearch('')
  }
  
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-slate-500 dark:text-slate-400">Icon</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
          >
            <FluentEmoji emoji={emoji || '✨'} size={24} />
            <span className="text-sm text-slate-600 dark:text-slate-300">
              {emoji ? changeLabel : emptyLabel}
            </span>
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-80 p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
        >
          <div className="p-3 space-y-3">
            {/* Header with Remove option */}
            {allowRemove && emoji && (
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Choose Emoji
                </p>
                <button
                  type="button"
                  onClick={handleRemove}
                  className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                >
                  Remove
                </button>
              </div>
            )}
            
            {/* Search Input */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search emojis... (e.g., run, book, sleep)"
                value={emojiSearch}
                onChange={(e) => setEmojiSearch(e.target.value)}
                className="w-full px-3 py-2 pl-9 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 dark:focus:ring-slate-500"
              />
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            {/* Emoji Grid - Scrollable */}
            {/* 
              5 fixes for scroll issues:
              1. data-vaul-no-drag - Tells vaul drawer NOT to capture drag events here
              2. touch-action: pan-y - Explicitly allow vertical panning
              3. overscroll-contain - Prevent scroll chaining to parent
              4. user-select: none - Prevent text selection interfering with scroll
              5. -webkit-overflow-scrolling: touch - iOS momentum scrolling
            */}
            <div 
              data-vaul-no-drag
              className="min-h-48 max-h-64 overflow-y-auto overscroll-contain"
              style={{
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
              onTouchMove={(e) => e.stopPropagation()}
              onWheel={(e) => e.stopPropagation()}
            >
              {displayEmojis.length > 0 ? (
                <div className="grid grid-cols-7 gap-1 p-1">
                  {displayEmojis.map((e) => (
                    <button
                      key={e}
                      type="button"
                      data-vaul-no-drag
                      onClick={() => handleSelect(e)}
                      className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all active:scale-90 ${
                        emoji === e
                          ? 'bg-slate-900 dark:bg-white'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <FluentEmoji emoji={e} size={22} />
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-center text-sm text-slate-400 py-4">No emojis found. Try a different search.</p>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

/** Compact variant for inline use (like EditHabitPage) */
interface CompactEmojiPickerProps {
  emoji: string | null
  onSelect: (emoji: string | null) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  allowRemove?: boolean
}

export function CompactEmojiPicker({ 
  emoji, 
  onSelect, 
  open, 
  onOpenChange,
  allowRemove = true,
}: CompactEmojiPickerProps) {
  const [emojiSearch, setEmojiSearch] = useState('')
  
  // Get emojis to display - search results or all emojis
  const displayEmojis = emojiSearch 
    ? searchEmojis(emojiSearch)
    : ALL_HABIT_EMOJIS
  
  const handleSelect = (selectedEmoji: string) => {
    onSelect(selectedEmoji)
    onOpenChange(false)
    setEmojiSearch('')
  }

  const handleRemove = () => {
    onSelect(null)
    onOpenChange(false)
    setEmojiSearch('')
  }
  
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-14 h-11 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-center transition-colors shrink-0"
        >
          {emoji ? (
            <FluentEmoji emoji={emoji} size={24} animated />
          ) : (
            <span className="text-slate-400 text-lg">+</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" align="start">
        <div className="p-3 space-y-3">
          {/* Header with Remove option */}
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Choose Emoji
            </p>
            {allowRemove && emoji && (
              <button
                type="button"
                onClick={handleRemove}
                className="text-xs text-slate-400 hover:text-red-500 transition-colors"
              >
                Remove
              </button>
            )}
          </div>
          
          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search emojis... (e.g., run, book, sleep)"
              value={emojiSearch}
              onChange={(e) => setEmojiSearch(e.target.value)}
              className="w-full px-3 py-2 pl-9 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 dark:focus:ring-slate-500"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          
          {/* Emoji Grid - Scrollable */}
          <div 
            data-vaul-no-drag
            className="min-h-48 max-h-64 overflow-y-auto overscroll-contain"
            style={{
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }}
            onTouchMove={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
          >
            {displayEmojis.length > 0 ? (
              <div className="grid grid-cols-7 gap-1 p-1">
                {displayEmojis.map((e) => (
                  <button
                    key={e}
                    type="button"
                    data-vaul-no-drag
                    onClick={() => handleSelect(e)}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all active:scale-90 ${
                      emoji === e
                        ? 'bg-violet-100 dark:bg-violet-900/30 ring-2 ring-violet-500'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <FluentEmoji emoji={e} size={22} animated={emoji === e} />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-center text-sm text-slate-400 py-4">No emojis found. Try a different search.</p>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
